import { useEffect } from 'react';
import SiteHeader from '../komponenter/SiteHeader';
import HomeAboutSection from '../komponenter/home/HomeAboutSection';
import HomeContactFooter from '../komponenter/home/HomeContactFooter';
import HomeErrorBanner from '../komponenter/home/HomeErrorBanner';
import HomeHeroSection from '../komponenter/home/HomeHeroSection';
import HomePropertyOverviewSection from '../komponenter/home/HomePropertyOverviewSection';
import HomePropertyTypesSection from '../komponenter/home/HomePropertyTypesSection';
import { useHomePage } from '../hooks/useHomePage';
import { useSeo } from '../hooks/useSeo';

function HomePage() {
  const {
    activeFilter,
    activeType,
    availableTypes,
    canBrowseProperties,
    currentPage,
    filteredProperties,
    goToNextPage,
    goToPage,
    goToPreviousPage,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
    handleTypeChange,
    isLoading,
    loadError,
    normalizedStartIndex,
    portfolioStats,
    resetFilters,
    searchTerm,
    settings,
    sortKey,
    totalPages,
    visibleProperties,
  } = useHomePage();

  useSeo({
    canonicalPath: '/',
    description:
      'OmsorgBygg.no leier ut boliger og omsorgsbygg over hele Norge. Se ledige boliger med adresse, størrelse, leie og tilgjengelighet.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          name: 'OmsorgBygg.no',
          url: 'https://omsorgbygg.no/',
        },
        {
          '@type': 'ItemList',
          itemListElement: filteredProperties.slice(0, 12).map((property, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: `https://omsorgbygg.no/eiendom/${property.slug}`,
            name: property.name,
          })),
          name: 'Ledige boliger hos OmsorgBygg',
        },
      ],
    },
    title: 'OmsorgBygg.no | Boliger og bygg til leie i hele Norge',
  });

  useEffect(() => {
    const revealElements = Array.from(document.querySelectorAll('[data-reveal]'));

    if (revealElements.length === 0) {
      return undefined;
    }

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      revealElements.forEach((element) => {
        element.classList.add('is-visible');
      });
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -48px 0px' }
    );

    revealElements.forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [isLoading, visibleProperties.length]);

  const portfolioReturnUrl = `/?filter=${activeFilter}&start=${normalizedStartIndex}#eiendommer`;

  return (
    <div className="text-forest-800">
      <SiteHeader />
      <HomeHeroSection
        heroDescription={settings.heroDescription}
        heroTitle={settings.heroTitle}
        portfolioStats={portfolioStats}
      />

      <main id="hovedinnhold" tabIndex="-1">
        <HomeErrorBanner loadError={loadError} />
        <HomeAboutSection />
        <HomePropertyTypesSection propertyTypes={settings.propertyTypes} />
        <HomePropertyOverviewSection
          activeFilter={activeFilter}
          activeType={activeType}
          availableTypes={availableTypes}
          canBrowseProperties={canBrowseProperties}
          currentPage={currentPage}
          filteredProperties={filteredProperties}
          goToNextPage={goToNextPage}
          goToPage={goToPage}
          goToPreviousPage={goToPreviousPage}
          handleFilterChange={handleFilterChange}
          handleSearchChange={handleSearchChange}
          handleSortChange={handleSortChange}
          handleTypeChange={handleTypeChange}
          isLoading={isLoading}
          portfolioReturnUrl={portfolioReturnUrl}
          resetFilters={resetFilters}
          searchTerm={searchTerm}
          sortKey={sortKey}
          totalPages={totalPages}
          visibleProperties={visibleProperties}
        />
      </main>
      <HomeContactFooter />
    </div>
  );
}

export default HomePage;